import { useState, useEffect } from 'react';
import { Shield, ExternalLink, Loader2, Mail, X } from 'lucide-react';
import axios from 'axios';

// Iter84 (Ankur 08-Jul-2026) — the Aurous support portal
// (`aurousorbit.ai`) does not yet have a valid TLS cert; clicking the
// header chip currently dumps testers onto a browser "connection is
// not secure" error page. Until the vendor cert is provisioned, the
// chip opens a small in-app modal with the correct contact channels
// AND (optionally, via `ENABLE_PORTAL`) offers a "Open portal anyway"
// escape hatch for internal QA. Flip `ENABLE_PORTAL` back to true once
// aurousorbit.ai has a valid HTTPS cert.
const PORTAL_URL = 'https://aurousorbit.ai/support/client/dashboard';
const ENABLE_PORTAL = false;
const SUPPORT_EMAIL = 'support@aurousorbit.ai';

export const SupportStatus = ({ API }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const checkStatus = async () => {
      // Check cache first (valid for 1 hour)
      const cached = sessionStorage.getItem('support_status');
      const cachedTime = sessionStorage.getItem('support_status_time');

      if (cached && cachedTime) {
        const age = Date.now() - parseInt(cachedTime, 10);
        if (age < 3600000) { // 1 hour
          setStatus(JSON.parse(cached));
          setLoading(false);
          return;
        }
      }

      try {
        const token = localStorage.getItem('hrms_token');
        if (!token) {
          setLoading(false);
          return;
        }

        const res = await axios.get(`${API}/api/support/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setStatus(res.data);
        sessionStorage.setItem('support_status', JSON.stringify(res.data));
        sessionStorage.setItem('support_status_time', Date.now().toString());
      } catch (e) {
        console.error('Failed to check support status:', e);
        setStatus({ has_active_support: false, status: 'error' });
      } finally {
        setLoading(false);
      }
    };

    checkStatus();
  }, [API]);

  const openSupport = () => {
    if (ENABLE_PORTAL) {
      window.open(PORTAL_URL, '_blank', 'noopener,noreferrer');
      return;
    }
    setModalOpen(true);
  };

  if (loading) {
    return (
      <button
        className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 text-xs font-medium"
        disabled
      >
        <Loader2 className="w-3 h-3 animate-spin" />
        <span>Checking...</span>
      </button>
    );
  }

  if (!status) return null;

  const isActive = status.has_active_support;
  const chipCls = isActive
    ? 'flex items-center space-x-1.5 px-3 py-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-medium hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors cursor-pointer'
    : 'flex items-center space-x-1.5 px-3 py-1.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-medium hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors cursor-pointer';

  return (
    <>
      <button
        onClick={openSupport}
        className={chipCls}
        data-testid={isActive ? 'support-status-active' : 'support-status-inactive'}
        title={isActive ? 'View support details' : 'Contact vendor support'}
      >
        <Shield className="w-3 h-3" />
        <span>{isActive ? 'Support Active' : 'Enable Support'}</span>
        <ExternalLink className="w-3 h-3" />
      </button>

      {/* Iter84 — Fallback in-app contact modal (rendered when
          ENABLE_PORTAL is false so tester doesn't hit the broken
          aurousorbit.ai TLS page). */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setModalOpen(false)}
          data-testid="support-modal-backdrop"
        >
          <div
            className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-emerald-500" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {isActive ? 'Support Active' : 'Contact Vendor Support'}
                </h3>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                aria-label="Close"
                data-testid="support-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
              <p>
                Our client support portal is currently being provisioned. In the
                meantime, please reach out to us via email and we&apos;ll respond
                within 1 business day.
              </p>
              <a
                href={`mailto:${SUPPORT_EMAIL}?subject=MyHR%20Orbit%20Support%20Request`}
                className="inline-flex items-center space-x-2 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
                data-testid="support-email-link"
              >
                <Mail className="w-4 h-4" />
                <span>{SUPPORT_EMAIL}</span>
              </a>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-3">
                Please include your company name, license ID and a brief
                description of the issue. Our team will get back to you shortly.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SupportStatus;
